import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Plus, Folder, FileText, Trash2, Edit3, Search, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'general', name: 'Allmänt', color: 'bg-slate-500' },
  { id: 'health', name: 'Hälsa', color: 'bg-emerald-500' },
  { id: 'work', name: 'Arbete', color: 'bg-blue-500' },
  { id: 'personal', name: 'Privat', color: 'bg-purple-500' },
  { id: 'ideas', name: 'Idéer', color: 'bg-amber-500' },
  { id: 'important', name: 'Viktigt', color: 'bg-red-500' },
];

export default function NotesPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState({ title: '', content: '', category: 'general' });
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${API}/api/notes`, { headers: getAuthHeader() });
      setNotes(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNote = async () => {
    if (!newNote.title.trim() && !newNote.content.trim()) return;
    
    try {
      if (editingNote) {
        await axios.put(`${API}/api/notes/${editingNote.id}`, newNote, { headers: getAuthHeader() });
        setNotes(notes.map(n => n.id === editingNote.id ? { ...n, ...newNote } : n));
        toast.success('Anteckning uppdaterad');
      } else {
        const res = await axios.post(`${API}/api/notes`, newNote, { headers: getAuthHeader() });
        setNotes([res.data, ...notes]);
        toast.success('Anteckning sparad');
      }
      closeEditor();
    } catch (error) {
      toast.error('Kunde inte spara');
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/api/notes/${noteId}`, { headers: getAuthHeader() });
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Anteckning borttagen');
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const openEditor = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNewNote({ title: note.title, content: note.content, category: note.category });
    } else {
      setEditingNote(null);
      setNewNote({ title: '', content: '', category: 'general' });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingNote(null);
    setNewNote({ title: '', content: '', category: 'general' });
  };

  const filteredNotes = notes.filter(note => {
    const matchesCategory = activeCategory === 'all' || note.category === activeCategory;
    const matchesSearch = !searchQuery || 
      note.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="notes-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Anteckningar</h1>
            <p className="text-xs text-muted-foreground">{notes.length} anteckningar</p>
          </div>
          <Button onClick={() => openEditor()} className="rounded-full" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Ny
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Sök anteckningar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-full"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant={activeCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveCategory('all')}
            className="rounded-full"
          >
            Alla
          </Button>
          {CATEGORIES.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveCategory(cat.id)}
              className="rounded-full whitespace-nowrap"
            >
              <span className={`w-2 h-2 rounded-full ${cat.color} mr-2`} />
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Notes grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-secondary/50 rounded-xl animate-pulse" />)}
          </div>
        ) : filteredNotes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredNotes.map(note => {
              const category = CATEGORIES.find(c => c.id === note.category);
              return (
                <div 
                  key={note.id}
                  className="bg-white rounded-xl p-4 border border-border/30 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => openEditor(note)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${category?.color || 'bg-slate-500'}`} />
                      <span className="text-xs text-muted-foreground">{category?.name || 'Allmänt'}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <h3 className="font-medium text-sm mb-1 line-clamp-1">
                    {note.title || 'Utan titel'}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-3">
                    {note.content}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(note.updated_at).toLocaleDateString('sv-SE')}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? 'Inga anteckningar hittades' : 'Skapa din första anteckning'}
            </p>
          </div>
        )}
      </main>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="font-heading font-semibold">
                {editingNote ? 'Redigera' : 'Ny anteckning'}
              </h3>
              <Button variant="ghost" size="sm" onClick={closeEditor} className="rounded-full w-8 h-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4 flex-1 overflow-y-auto">
              <Input
                placeholder="Titel"
                value={newNote.title}
                onChange={(e) => setNewNote({...newNote, title: e.target.value})}
                className="rounded-xl font-medium"
              />
              
              <div>
                <label className="text-sm font-medium mb-2 block">Kategori</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <Button
                      key={cat.id}
                      variant={newNote.category === cat.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewNote({...newNote, category: cat.id})}
                      className="rounded-full"
                    >
                      <span className={`w-2 h-2 rounded-full ${cat.color} mr-2`} />
                      {cat.name}
                    </Button>
                  ))}
                </div>
              </div>
              
              <Textarea
                placeholder="Skriv din anteckning här..."
                value={newNote.content}
                onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                className="rounded-xl min-h-[200px] resize-none"
              />
            </div>
            
            <div className="p-4 border-t border-border/30 flex gap-3">
              <Button variant="outline" onClick={closeEditor} className="flex-1 rounded-full">
                Avbryt
              </Button>
              <Button onClick={saveNote} className="flex-1 rounded-full">
                {editingNote ? 'Uppdatera' : 'Spara'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
